import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from './Button';
import { getBookedDates, fallbackBookedDates, BookedDate } from '../services/calendarService';
import { getPriceForDate } from '../config/priceConfig';

// Mois en français
const months = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

// Jours de la semaine en français
const weekdays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const Availability: React.FC = () => {
  const [guests, setGuests] = useState<number>(2);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] = useState<'start' | 'end'>('start');
  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [bookedDates, setBookedDates] = useState<BookedDate[]>(fallbackBookedDates);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Charger les dates réservées depuis le calendrier Airbnb
  useEffect(() => {
    const loadBookedDates = async () => {
      try {
        setIsLoading(true);
        console.log('Chargement des dates réservées...');
        
        const dates = await getBookedDates();
        console.log('Dates récupérées:', dates);
        
        if (dates.length > 0) {
          console.log('Utilisation des dates récupérées:', dates.length, 'périodes');
          setBookedDates(dates);
        } else {
          console.log('Aucune date récupérée, utilisation des dates de secours');
          // Utiliser les dates de secours si aucune date n'est récupérée
          setBookedDates(fallbackBookedDates);
        }
        setError(null);
      } catch (err) {
        console.error('Erreur lors du chargement des dates réservées:', err);
        setError('Impossible de charger les disponibilités. Veuillez réessayer plus tard.');
        setBookedDates(fallbackBookedDates);
      } finally {
        setIsLoading(false);
      }
    };

    loadBookedDates();
  }, []);

  // Fonction pour gérer le changement du nombre d'invités
  const handleGuestsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setGuests(parseInt(e.target.value));
  };

  // Fonction pour vérifier si une date est réservée
  const isDateBooked = (date: Date) => {
    const result = bookedDates.some(
      (booking) => {
        const isBooked = date >= booking.start && date <= booking.end;
        if (isBooked) {
          console.log('Date réservée:', date.toISOString(), 'dans la période:', 
                      booking.start.toISOString(), 'à', booking.end.toISOString());
        }
        return isBooked;
      }
    );
    return result;
  };

  // Fonction pour générer les jours du mois
  const generateDays = () => {
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
    const days = [];
    
    // Ajuster pour que lundi soit le premier jour (0)
    const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    
    // Jours vides pour aligner le calendrier
    for (let i = 0; i < adjustedFirstDay; i++) {
      days.push(<DayCell key={`empty-${i}`} empty />);
    }
    
    // Jours du mois
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentYear, currentMonth, i);
      const dateString = `${i}/${currentMonth + 1}/${currentYear}`;
      const isBooked = isDateBooked(date);
      const isSelected = selectedDates.includes(dateString);
      const isPast = date < new Date();
      
      days.push(
        <DayCell 
          key={i} 
          booked={isBooked}
          selected={isSelected}
          disabled={isBooked || isPast}
          onClick={() => {
            if (!isBooked && !isPast) {
              if (selectionMode === 'start') {
                // Premier clic : sélectionner la date d'arrivée
                setSelectedDates([dateString]);
                setSelectionMode('end');
              } else {
                // Deuxième clic : sélectionner la date de départ
                // Vérifier que la date de départ est après la date d'arrivée
                if (selectedDates.length > 0) {
                  const startDateParts = selectedDates[0].split('/').map(Number);
                  const startDate = new Date(startDateParts[2], startDateParts[1] - 1, startDateParts[0]);
                  const endDateParts = dateString.split('/').map(Number);
                  const endDate = new Date(endDateParts[2], endDateParts[1] - 1, endDateParts[0]);
                  
                  if (endDate >= startDate) {
                    // Générer toutes les dates entre la date d'arrivée et la date de départ
                    const allDates = [];
                    const currentDate = new Date(startDate);
                    
                    // Ajouter la date d'arrivée
                    allDates.push(selectedDates[0]);
                    
                    // Ajouter les dates intermédiaires
                    while (true) {
                      currentDate.setDate(currentDate.getDate() + 1);
                      if (currentDate > endDate) break;
                      
                      const day = currentDate.getDate();
                      const month = currentDate.getMonth() + 1;
                      const year = currentDate.getFullYear();
                      allDates.push(`${day}/${month}/${year}`);
                    }
                    
                    setSelectedDates(allDates);
                    setSelectionMode('start');
                  } else {
                    // Si la date de départ est avant la date d'arrivée, on recommence
                    setSelectedDates([dateString]);
                    setSelectionMode('end');
                  }
                }
              }
            }
          }}
        >
          {i}
        </DayCell>
      );
    }
    
    return days;
  };

  // Fonction pour passer au mois précédent
  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  // Fonction pour passer au mois suivant
  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Fonction pour calculer le prix total
  const calculateTotalPrice = () => {
    if (selectedDates.length === 0) return { totalPrice: 0, specialPeriodInfo: null };
    
    // Calculer le prix pour chaque date sélectionnée
    let totalPrice = 0;
    let specialPeriodInfo = null;
    
    for (const dateString of selectedDates) {
      // Convertir la chaîne de date en objet Date
      const [day, month, year] = dateString.split('/').map(Number);
      const date = new Date(year, month - 1, day); // month - 1 car les mois commencent à 0
      
      // Obtenir le prix pour cette date
      const { price, periodName } = getPriceForDate(date);
      totalPrice += price;
      
      // Stocker l'info de période spéciale si applicable
      if (periodName && periodName !== "Haute saison" && 
          periodName !== "Moyenne saison" && periodName !== "Basse saison") {
        specialPeriodInfo = { name: periodName, price };
      }
    }
    
    return { totalPrice, specialPeriodInfo };
  };

  return (
    <AvailabilityContainer>
      <AvailabilityTitle>Vérifier la disponibilité</AvailabilityTitle>
      
      {isLoading ? (
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Chargement des disponibilités...</LoadingText>
        </LoadingContainer>
      ) : error ? (
        <ErrorContainer>
          <ErrorMessage>{error}</ErrorMessage>
          <Button 
            onClick={() => {
              setIsLoading(true);
              setError(null);
              getBookedDates()
                .then(dates => {
                  if (dates.length > 0) {
                    setBookedDates(dates);
                  } else {
                    setBookedDates(fallbackBookedDates);
                  }
                })
                .catch(err => {
                  console.error('Erreur lors du rechargement des dates:', err);
                  setError('Impossible de charger les disponibilités. Veuillez réessayer plus tard.');
                  setBookedDates(fallbackBookedDates);
                })
                .finally(() => setIsLoading(false));
            }}
          >
            Réessayer
          </Button>
        </ErrorContainer>
      ) : (
        <AvailabilityForm>
        <FormGroup>
          <FormLabel>Dates</FormLabel>
          <CalendarContainer>
            <CalendarHeader>
              <MonthNavButton onClick={prevMonth}>&lt;</MonthNavButton>
              <MonthTitle>{months[currentMonth]} {currentYear}</MonthTitle>
              <MonthNavButton onClick={nextMonth}>&gt;</MonthNavButton>
            </CalendarHeader>
            <WeekdaysRow>
              {weekdays.map(day => (
                <WeekdayCell key={day}>{day}</WeekdayCell>
              ))}
            </WeekdaysRow>
            <DaysGrid>
              {generateDays()}
            </DaysGrid>
          </CalendarContainer>
        </FormGroup>
        
        <FormGroup>
          <FormLabel>Nombre d'invités</FormLabel>
          <Select value={guests} onChange={handleGuestsChange}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
              <option key={num} value={num}>{num} {num === 1 ? 'personne' : 'personnes'}</option>
            ))}
          </Select>
        </FormGroup>
        
        {selectedDates.length > 0 && (
          <SummaryContainer>
            <SummaryTitle>Résumé de votre séjour</SummaryTitle>
            <SummaryItem>
              <span>Dates sélectionnées:</span>
              <strong>{selectedDates.length} nuits</strong>
            </SummaryItem>
            <SummaryItem>
              <span>Invités:</span>
              <strong>{guests} {guests === 1 ? 'personne' : 'personnes'}</strong>
            </SummaryItem>
            {(() => {
              const priceInfo = calculateTotalPrice();
              return (
                <>
                  <SummaryTotal>
                    <span>Tarif estimé:</span>
                    <strong>{priceInfo.totalPrice}€</strong>
                  </SummaryTotal>
                  <CleaningFees>+ 200 € de frais de ménage</CleaningFees>
                  
                  {priceInfo.specialPeriodInfo && priceInfo.specialPeriodInfo.name && (
                    <SpecialPeriodNote>
                      *Inclut la période spéciale "{priceInfo.specialPeriodInfo.name}" 
                      à {priceInfo.specialPeriodInfo.price}€/nuit
                    </SpecialPeriodNote>
                  )}
                </>
              );
            })()}
            
            <Button 
              to={`/contact?startDate=${selectedDates.length > 0 ? selectedDates[0] : ''}&endDate=${selectedDates.length > 0 ? selectedDates[selectedDates.length - 1] : ''}&guests=${guests}`} 
              fullWidth
            >
              Faire une demande d'informations
            </Button>
          </SummaryContainer>
        )}
        </AvailabilityForm>
      )}
      
      <AvailabilityLegend>
        <LegendItem>
          <LegendColor color="var(--color-accent)" />
          <span>Sélectionné</span>
        </LegendItem>
        <LegendItem>
          <LegendColor color="#f0f0f0" />
          <span>Non disponible</span>
        </LegendItem>
        <LegendItem>
          <LegendColor color="#ffffff" border />
          <span>Disponible</span>
        </LegendItem>
      </AvailabilityLegend>
    </AvailabilityContainer>
  );
};

const AvailabilityContainer = styled.div`
  background-color: var(--color-light);
  padding: 4rem;
  box-shadow: var(--shadow);
`;

const AvailabilityTitle = styled.h3`
  font-size: 2.4rem;
  margin-bottom: 3rem;
  text-align: center;
`;

const AvailabilityForm = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  
  @media (min-width: 992px) {
    grid-template-columns: 2fr 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 2rem;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 1.6rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const CalendarContainer = styled.div`
  background-color: white;
  box-shadow: var(--shadow);
  padding: 2rem;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const MonthTitle = styled.h4`
  font-size: 1.8rem;
  margin: 0;
`;

const MonthNavButton = styled.button`
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: var(--color-primary);
  
  &:hover {
    color: var(--color-accent);
  }
`;

const WeekdaysRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 1rem;
`;

const WeekdayCell = styled.div`
  text-align: center;
  font-weight: 600;
  font-size: 1.4rem;
  color: var(--color-secondary);
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
`;

interface DayCellProps {
  empty?: boolean;
  booked?: boolean;
  selected?: boolean;
  disabled?: boolean;
}

const DayCell = styled.div<DayCellProps>`
  height: 4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  cursor: ${({ disabled, empty }) => (disabled || empty ? 'default' : 'pointer')};
  background-color: ${({ booked, selected, empty }) => {
    if (empty) return 'transparent';
    if (booked) return '#f0f0f0';
    if (selected) return 'var(--color-accent)';
    return 'white';
  }};
  color: ${({ booked, selected, empty }) => {
    if (empty) return 'transparent';
    if (booked) return '#aaa';
    if (selected) return 'white';
    return 'var(--color-primary)';
  }};
  border: 1px solid ${({ empty }) => (empty ? 'transparent' : 'rgba(0, 0, 0, 0.1)')};
  
  &:hover {
    background-color: ${({ disabled, empty, selected }) => {
      if (disabled || empty) return;
      if (selected) return 'var(--color-accent)';
      return 'rgba(200, 169, 126, 0.2)';
    }};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 1.2rem;
  font-size: 1.6rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0;
  background-color: white;
  font-family: var(--font-secondary);
  
  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const SummaryContainer = styled.div`
  background-color: white;
  padding: 3rem;
  box-shadow: var(--shadow);
  
  @media (min-width: 992px) {
    grid-column: 2;
    grid-row: 1 / span 2;
  }
`;

const SummaryTitle = styled.h4`
  font-size: 2rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  font-size: 1.6rem;
  
  span {
    color: var(--color-secondary);
  }
  
  strong {
    font-weight: 600;
  }
`;

const SummaryTotal = styled(SummaryItem)`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 1.8rem;
  
  strong {
    font-size: 2.2rem;
    color: var(--color-accent);
  }
`;

// Composant pour afficher les informations sur les périodes spéciales
const SpecialPeriodNote = styled.div`
  font-size: 1.4rem;
  color: var(--color-accent);
  margin-top: 1rem;
  margin-bottom: 2rem;
  font-style: italic;
  text-align: center;
`;

const AvailabilityLegend = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 3rem;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 1.4rem;
`;

const LegendColor = styled.div<{ color: string; border?: boolean }>`
  width: 2rem;
  height: 2rem;
  background-color: ${({ color }) => color};
  border: ${({ border }) => border ? '1px solid rgba(0, 0, 0, 0.1)' : 'none'};
`;

// Composants pour l'état de chargement
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  background-color: white;
  box-shadow: var(--shadow);
`;

const LoadingSpinner = styled.div`
  width: 4rem;
  height: 4rem;
  border: 0.5rem solid rgba(200, 169, 126, 0.2);
  border-top: 0.5rem solid var(--color-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 2rem;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: 1.6rem;
  color: var(--color-secondary);
`;

// Composants pour l'état d'erreur
const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  background-color: white;
  box-shadow: var(--shadow);
`;

const ErrorMessage = styled.p`
  font-size: 1.6rem;
  color: #e74c3c;
  margin-bottom: 2rem;
  text-align: center;
`;

// Composant pour les frais de ménage (style plus discret)
const CleaningFees = styled.div`
  font-size: 1.4rem;
  color: var(--color-secondary);
  font-weight: normal;
  margin-top: 0.5rem;
`;

export default Availability;
